import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MainNavigation from '../../components/ui/MainNavigation';
import NavigationBreadcrumb from '../../components/ui/NavigationBreadcrumb';
import FooterSection from '../homepage/components/FooterSection';

const DecisionGuides = () => {
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const guides = [
    {
      id: 'solar-decision',
      title: 'Zijn zonnepanelen geschikt voor mijn huis?',
      icon: 'Sun',
      color: 'from-yellow-400 to-orange-500',
      description: 'Ontdek in 5 stappen of zonnepanelen een slimme investering zijn voor uw situatie',
      steps: [
        {
          question: 'Wat is de oriëntatie van uw dak?',
          options: [
            { value: 'south', label: 'Zuid (ideaal)', score: 10 },
            { value: 'southeast-southwest', label: 'Zuidoost of Zuidwest', score: 8 },
            { value: 'east-west', label: 'Oost of West', score: 6 },
            { value: 'north', label: 'Noord', score: 2 },
          ],
        },
        {
          question: 'Hoeveel schaduw valt er op uw dak?',
          options: [
            { value: 'none', label: 'Geen schaduw', score: 10 },
            { value: 'morning-evening', label: 'Alleen \'s ochtends of \'s avonds', score: 7 },
            { value: 'partial', label: 'Gedeeltelijk overdag', score: 4 },
            { value: 'full', label: 'Veel schaduw', score: 1 },
          ],
        },
        {
          question: 'Wat is uw jaarlijks energieverbruik?',
          options: [
            { value: 'high', label: 'Hoog (>4000 kWh)', score: 10 },
            { value: 'medium', label: 'Gemiddeld (2500-4000 kWh)', score: 8 },
            { value: 'low', label: 'Laag (<2500 kWh)', score: 5 },
          ],
        },
        {
          question: 'Wat is de staat van uw dak?',
          options: [
            { value: 'excellent', label: 'Uitstekend (nieuw of recent vervangen)', score: 10 },
            { value: 'good', label: 'Goed (geen renovatie nodig)', score: 8 },
            { value: 'moderate', label: 'Redelijk (renovatie binnen 5-10 jaar)', score: 4 },
            { value: 'poor', label: 'Slecht (renovatie nodig)', score: 1 },
          ],
        },
        {
          question: 'Wat is uw budget?',
          options: [
            { value: 'high', label: '€8.000 - €12.000+', score: 10 },
            { value: 'medium', label: '€5.000 - €8.000', score: 7 },
            { value: 'low', label: '€3.000 - €5.000', score: 4 },
          ],
        },
      ],
    },
    {
      id: 'insulation-priority',
      title: 'Welke isolatie moet ik eerst aanpakken?',
      icon: 'Home',
      color: 'from-blue-400 to-cyan-500',
      description: 'Bepaal de optimale isolatievolgorde voor maximale besparing',
      steps: [
        {
          question: 'Is uw dak geïsoleerd?',
          options: [
            { value: 'no', label: 'Nee, niet geïsoleerd', score: 10, priority: 'dak' },
            { value: 'partial', label: 'Gedeeltelijk', score: 5, priority: 'dak' },
            { value: 'yes', label: 'Ja, goed geïsoleerd', score: 0 },
          ],
        },
        {
          question: 'Zijn uw muren geïsoleerd?',
          options: [
            { value: 'no', label: 'Nee, spouwmuren niet geïsoleerd', score: 8, priority: 'muren' },
            { value: 'partial', label: 'Gedeeltelijk', score: 4, priority: 'muren' },
            { value: 'yes', label: 'Ja, goed geïsoleerd', score: 0 },
          ],
        },
        {
          question: 'Is uw vloer geïsoleerd?',
          options: [
            { value: 'no', label: 'Nee, koude vloeren', score: 6, priority: 'vloer' },
            { value: 'partial', label: 'Gedeeltelijk', score: 3, priority: 'vloer' },
            { value: 'yes', label: 'Ja, goed geïsoleerd', score: 0 },
          ],
        },
        {
          question: 'Heeft u dubbel glas?',
          options: [
            { value: 'single', label: 'Nee, enkel glas', score: 7, priority: 'glas' },
            { value: 'double', label: 'Ja, dubbel glas', score: 3, priority: 'glas' },
            { value: 'hrpp', label: 'Ja, HR++ glas', score: 0 },
          ],
        },
      ],
    },
    {
      id: 'heatpump-readiness',
      title: 'Is mijn huis klaar voor een warmtepomp?',
      icon: 'Thermometer',
      color: 'from-green-400 to-emerald-500',
      description: 'Check of uw woning geschikt is voor een warmtepomp',
      steps: [
        {
          question: 'Wat is het energielabel van uw woning?',
          options: [
            { value: 'a-b', label: 'A of B (goed geïsoleerd)', score: 10 },
            { value: 'c', label: 'C (redelijk geïsoleerd)', score: 6 },
            { value: 'd-e', label: 'D of E (matig geïsoleerd)', score: 3 },
            { value: 'f-g', label: 'F of G (slecht geïsoleerd)', score: 0 },
          ],
        },
        {
          question: 'Welk verwarmingssysteem heeft u nu?',
          options: [
            { value: 'floor', label: 'Vloerverwarming', score: 10 },
            { value: 'lowtemp', label: 'Lagetemperatuur radiatoren', score: 8 },
            { value: 'standard', label: 'Standaard radiatoren', score: 5 },
            { value: 'old', label: 'Oude radiatoren', score: 2 },
          ],
        },
        {
          question: 'Heeft u ruimte voor een buitenunit?',
          options: [
            { value: 'yes-quiet', label: 'Ja, op rustige plek', score: 10 },
            { value: 'yes-limited', label: 'Ja, maar beperkt', score: 7 },
            { value: 'difficult', label: 'Lastig, weinig ruimte', score: 3 },
          ],
        },
      ],
    },
  ];

  const handleAnswerSelect = (stepIndex, option) => {
    setAnswers({ ...answers, [stepIndex]: option });
    if (stepIndex < selectedGuide?.steps?.length - 1) {
      setTimeout(() => setCurrentStep(stepIndex + 1), 300);
    }
  };

  const calculateResult = () => {
    const totalScore = Object.values(answers)?.reduce((sum, answer) => sum + (answer?.score || 0), 0);
    const maxScore = selectedGuide?.steps?.reduce((sum, step) => {
      const maxStepScore = Math.max(...step?.options?.map(opt => opt?.score));
      return sum + maxStepScore;
    }, 0);
    const percentage = (totalScore / maxScore) * 100;

    if (percentage >= 80) {
      return {
        verdict: 'Uitstekend geschikt! ✅',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        message: 'Uw situatie is ideaal. We raden aan om snel te starten.',
        action: 'Vraag offertes aan',
      };
    } else if (percentage >= 60) {
      return {
        verdict: 'Geschikt met aandachtspunten ⚠️',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        message: 'Het kan, maar er zijn enkele verbeterpunten. Laat u goed adviseren.',
        action: 'Vraag advies aan',
      };
    } else {
      return {
        verdict: 'Eerst voorbereidingen nodig ❌',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        message: 'We raden aan om eerst andere stappen te nemen voordat u hiermee start.',
        action: 'Bekijk alternatieven',
      };
    }
  };

  const resetGuide = () => {
    setCurrentStep(0);
    setAnswers({});
  };

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      <NavigationBreadcrumb />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-16 relative overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-10"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{ duration: 20, repeat: Infinity, repeatType: 'reverse' }}
          style={{
            backgroundImage: 'radial-gradient(circle, #3b82f6 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <motion.div
              className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full mb-6"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Icon name="CheckSquare" size={20} />
              <span className="font-semibold">Praktische Beslisgidsen</span>
            </motion.div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Maak de <span className="text-blue-600">juiste keuze</span> in 5 minuten
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Interactieve tools die u stap voor stap helpen bij belangrijke beslissingen over verduurzaming.
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={18} className="text-blue-600" />
                <span>5 minuten per gids</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Target" size={18} className="text-blue-600" />
                <span>Persoonlijk advies</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Guide Selection or Active Guide */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatePresence mode="wait">
            {!selectedGuide ? (
              <motion.div
                key="guide-selection"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {guides?.map((guide, index) => (
                  <motion.div
                    key={guide?.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-2xl shadow-xl overflow-hidden cursor-pointer"
                    style={{
                      transform: 'perspective(1000px) rotateX(0deg)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1), 0 1px 8px rgba(0,0,0,0.06)'
                    }}
                    whileHover={{
                      y: -10,
                      boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 5px 15px rgba(0,0,0,0.1)'
                    }}
                    onClick={() => setSelectedGuide(guide)}
                  >
                    <div className={`h-32 bg-gradient-to-br ${guide?.color} flex items-center justify-center`}>
                      <Icon name={guide?.icon} size={64} className="text-white" />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{guide?.title}</h3>
                      <p className="text-gray-600 mb-4">{guide?.description}</p>
                      <div className="flex items-center text-blue-600 font-semibold">
                        <span>Start gids</span>
                        <Icon name="ArrowRight" size={18} className="ml-2" />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="active-guide"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="max-w-3xl mx-auto"
              >
                {/* Progress Bar */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-600">
                      Stap {currentStep + 1} van {selectedGuide?.steps?.length}
                    </span>
                    <button
                      onClick={() => {
                        setSelectedGuide(null);
                        resetGuide();
                      }}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Terug naar overzicht
                    </button>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div
                      className={`h-full bg-gradient-to-r ${selectedGuide?.color}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentStep + 1) / selectedGuide?.steps?.length) * 100}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>

                {/* Question Card */}
                {currentStep < selectedGuide?.steps?.length ? (
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    className="bg-white rounded-2xl shadow-2xl p-8"
                  >
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">
                      {selectedGuide?.steps?.[currentStep]?.question}
                    </h2>
                    <div className="space-y-3">
                      {selectedGuide?.steps?.[currentStep]?.options?.map((option, index) => (
                        <motion.button
                          key={index}
                          onClick={() => handleAnswerSelect(currentStep, option)}
                          className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                            answers?.[currentStep]?.value === option?.value
                              ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                          }`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">{option?.label}</span>
                            {answers?.[currentStep]?.value === option?.value && (
                              <Icon name="CheckCircle" size={24} className="text-blue-500" />
                            )}
                          </div>
                        </motion.button>
                      ))}
                    </div>

                    {/* Navigation */}
                    {currentStep > 0 && (
                      <button
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="mt-6 text-gray-600 hover:text-gray-900 flex items-center"
                      >
                        <Icon name="ArrowLeft" size={18} className="mr-2" />
                        Vorige vraag
                      </button>
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white rounded-2xl shadow-2xl p-8"
                  >
                    {(() => {
                      const result = calculateResult();
                      return (
                        <div className="text-center">
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', duration: 0.6 }}
                            className={`inline-flex items-center justify-center w-24 h-24 rounded-full ${result?.bgColor} mb-6`}
                          >
                            <span className="text-4xl">
                              {result?.verdict?.includes('✅') ? '✅' : result?.verdict?.includes('⚠️') ? '⚠️' : '❌'}
                            </span>
                          </motion.div>
                          <h2 className={`text-3xl font-bold mb-4 ${result?.color}`}>
                            {result?.verdict}
                          </h2>
                          <p className="text-xl text-gray-600 mb-8">{result?.message}</p>
                          <div className="flex items-center justify-center space-x-4">
                            <Button size="lg">{result?.action}</Button>
                            <Button
                              size="lg"
                              variant="outline"
                              onClick={() => {
                                setSelectedGuide(null);
                                resetGuide();
                              }}
                            >
                              Andere gids
                            </Button>
                          </div>
                        </div>
                      );
                    })()}
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
      <FooterSection />
    </div>
  );
};

export default DecisionGuides;